import React, { useState, useRef, useEffect } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import '../styles/footer-preview.css';
import { toast } from 'react-toastify';

const FooterEditor = ({ value, onChange, placeholder = "Design your footer..." }) => {
  const [editorHtml, setEditorHtml] = useState(value || '');
  const quillRef = useRef(null);

  useEffect(() => {
    setEditorHtml(value || '');
  }, [value]);

  const handleChange = (html) => {
    setEditorHtml(html);
    onChange(html);
  };

  const modules = {
    toolbar: {
      container: [
        [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
        [{ 'font': [] }],
        [{ 'size': ['small', false, 'large', 'huge'] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'script': 'sub' }, { 'script': 'super' }],
        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
        [{ 'indent': '-1' }, { 'indent': '+1' }],
        [{ 'direction': 'rtl' }],
        [{ 'align': [] }],
        ['link', 'image', 'video'],
        ['blockquote', 'code-block'],
        ['clean']
      ]
    },
    clipboard: {
      matchVisual: false,
    }
  };

  const formats = [
    'header', 'font', 'size',
    'bold', 'italic', 'underline', 'strike',
    'color', 'background',
    'script',
    'list', 'bullet', 'indent',
    'direction', 'align',
    'link', 'image', 'video',
    'blockquote', 'code-block'
  ];

  // Custom toolbar handlers
  const insertTemplate = (template) => {
    const quill = quillRef.current.getEditor();
    const range = quill.getSelection();
    if (range) {
      quill.clipboard.dangerouslyPasteHTML(range.index, template);
    }
  };

  const footerTemplates = [
    {
      name: 'TwilightX Mail Professional',
      html: `
        <div style="text-align: center; padding: 30px 20px; background-color: #f8f9fa; border-top: 3px solid #007bff; font-family: Arial, sans-serif;">
          <h3 style="color: #007bff; margin-bottom: 15px; font-size: 18px;">TwilightX Mail</h3>
          <p style="margin: 8px 0; color: #666; font-size: 14px;">For support, write to <a href="mailto:support@twilightxmail.com" style="color: #007bff; text-decoration: none;">support@twilightxmail.com</a></p>
          <div style="margin: 15px 0; padding: 15px 0; border-top: 1px solid #dee2e6;">
            <p style="margin: 5px 0; color: #666; font-size: 13px;">📧 info@twilightfinland.eu</p>
            <p style="margin: 5px 0; color: #666; font-size: 13px;">📞 +358 44981 5552</p>
            <p style="margin: 5px 0; color: #666; font-size: 13px;">🌐 <a href="https://twilightfinland.eu/" style="color: #007bff; text-decoration: none;">https://twilightfinland.eu/</a></p>
          </div>
          <div style="margin-top: 20px; padding-top: 15px; border-top: 1px solid #dee2e6;">
            <p style="margin: 10px 0; color: #666; font-size: 12px;">© 2025 TwilightX Mail. All rights reserved.</p>
          </div>
        </div>
      `
    },
    {
      name: 'TwilightX Mail Simple',
      html: `
        <div style="text-align: center; padding: 20px; background-color: #ffffff; border-top: 1px solid #e9ecef; font-family: Arial, sans-serif;">
          <p style="margin: 5px 0; color: #666; font-size: 14px;">Warm regards,</p>
          <p style="margin: 5px 0; color: #333; font-size: 16px; font-weight: bold;">TwilightX Mail Team</p>
          <p style="margin: 15px 0 5px 0; color: #666; font-size: 12px;">© 2025 TwilightX Mail. All rights reserved.</p>
        </div>
      `
    },
    {
      name: 'TwilightX Mail Branded',
      html: `
        <div style="padding: 30px; background: linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%); color: white; font-family: Arial, sans-serif;">
          <div style="text-align: center; margin-bottom: 20px;">
            <h2 style="margin-bottom: 15px; color: white; font-size: 24px;">TWILIGHTX MAIL</h2>
            <p style="margin: 5px 0; font-size: 14px; opacity: 0.9;">Advanced Email Management Platform</p>
          </div>
          <div style="display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; margin: 20px 0;">
            <div style="flex: 1; min-width: 200px; text-align: center;">
              <p style="margin: 5px 0; font-size: 14px;">📧 info@twilightfinland.eu</p>
              <p style="margin: 5px 0; font-size: 14px;">📞 +358 44981 5552</p>
              <p style="margin: 5px 0; font-size: 14px;">🌐 twilightfinland.eu</p>
            </div>
          </div>
          <hr style="margin: 20px 0; border: none; border-top: 1px solid rgba(255,255,255,0.3);">
          <p style="text-align: center; font-size: 12px; opacity: 0.8;">© 2025 TwilightX Mail. All rights reserved.</p>
        </div>
      `
    },
    {
      name: 'Generic Professional',
      html: `
        <div style="text-align: center; padding: 20px; background-color: #f8f9fa; border-top: 3px solid #007bff;">
          <h3 style="color: #007bff; margin-bottom: 10px;">Your Company Name</h3>
          <p style="margin: 5px 0; color: #666;">123 Business Street, City, State 12345</p>
          <p style="margin: 5px 0; color: #666;">Phone: (555) 123-4567 | Email: info@company.com</p>
          <p style="margin: 10px 0; color: #666;">© 2025 Your Company Name. All rights reserved.</p>
        </div>
      `
    }
  ];

  return (
    <div className="footer-editor">
      {/* Template Buttons */}
      <div className="mb-4 p-3 bg-gray-50 rounded-lg">
        <h4 className="text-sm font-medium text-gray-700 mb-2">Quick Templates:</h4>
        <div className="flex flex-wrap gap-2">
          {footerTemplates.map((template, index) => (
            <button
              key={index}
              type="button"
              onClick={() => insertTemplate(template.html)}
              className="px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
            >
              {template.name}
            </button>
          ))}
        </div>
      </div>

      {/* Rich Text Editor */}
      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={editorHtml}
        onChange={handleChange}
        modules={modules}
        formats={formats}
        placeholder={placeholder}
        style={{
          height: '300px',
          marginBottom: '50px'
        }}
      />

      {/* Enhanced Preview */}
      {editorHtml && (
        <div className="mt-8">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-medium text-gray-700">Live Preview:</h4>
            <div className="flex gap-2">
              <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">Desktop View</span>
            </div>
          </div>
          
          {/* Email Container Preview */}
          <div className="email-preview-container">
            {/* Simulated Email Header */}
            <div className="email-preview-header">
              <div className="text-sm text-gray-600 mb-2">📧 Email Preview</div>
              <div className="text-lg font-semibold text-gray-900 mb-1">Welcome to TwilightX Mail</div>
              <div className="text-sm text-gray-500">From: noreply@twilightxmail.com</div>
            </div>
            
            {/* Email Body Simulation */}
            <div className="email-preview-body">
              <div className="text-gray-700 leading-relaxed">
                <p className="mb-4">Dear Valued Partner,</p>
                <p className="mb-4">Thank you for your interest in TwilightX Mail's email management platform. We're excited to help you streamline your email communications.</p>
                <p className="mb-4">This email demonstrates how your footer will appear in actual communications with your contacts.</p>
                <p className="mb-4">Best regards,<br />TwilightX Mail Team</p>
              </div>
            </div>
            
            {/* Footer Preview */}
            <div className="email-preview-footer">
              <div 
                className="footer-preview"
                dangerouslySetInnerHTML={{ __html: editorHtml }}
              />
            </div>
          </div>
          
          {/* Mobile Preview */}
          <div className="mt-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-medium text-gray-700">Mobile Preview:</h4>
              <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">Mobile View</span>
            </div>
            
            <div className="max-w-sm mx-auto email-preview-container">
              <div className="bg-white">
                {/* Mobile Email Header */}
                <div className="email-preview-header">
                  <div className="text-xs text-gray-600 mb-1">📱 Mobile Email</div>
                  <div className="text-sm font-semibold text-gray-900 mb-1 truncate">Welcome to TwilightX Mail</div>
                  <div className="text-xs text-gray-500 truncate">From: noreply@twilightxmail.com</div>
                </div>
                
                {/* Mobile Email Body */}
                <div className="email-preview-body">
                  <div className="text-sm text-gray-700 leading-relaxed">
                    <p className="mb-2">Dear Partner,</p>
                    <p className="mb-2">Thank you for your interest in our programs...</p>
                    <p className="mb-2">Best regards,<br />TwilightX Mail Team</p>
                  </div>
                </div>
                
                {/* Mobile Footer Preview */}
                <div className="email-preview-footer">
                  <div 
                    className="footer-preview-mobile"
                    dangerouslySetInnerHTML={{ __html: editorHtml }}
                  />
                </div>
              </div>
            </div>
          </div>
          
          {/* HTML Code Preview */}
          <div className="mt-6">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-medium text-gray-700">HTML Code:</h4>
              <button
                type="button"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(editorHtml);
                    toast.success('HTML code copied to clipboard!');
                  } catch (err) {
                    toast.error('Failed to copy HTML code');
                  }
                }}
                className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded hover:bg-blue-200 transition-colors"
              >
                Copy HTML
              </button>
            </div>
            <pre className="bg-gray-900 text-green-400 p-4 rounded-lg text-xs overflow-x-auto max-h-40">
              <code>{editorHtml}</code>
            </pre>
          </div>
        </div>
      )}
    </div>
  );
};

export default FooterEditor;